import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";

// General Play: a member registers intent to play a round outside any
// organised competition (e.g. a club-announced open comp, or just playing
// whenever they like), then returns to the kiosk afterward to enter their
// score. This page is the admin side: pick the default course General
// Play rounds are played on, turn the feature on/off, and review every
// round ever registered. Rounds are recorded for reference only — they
// do not feed back into anyone's society handicap automatically (confirmed
// with Mike 2026-10-19; a society handicap recorded here is also not an
// official WHS Handicap Index).
export default function GeneralPlay() {
  const [settings, setSettings] = useState(null);
  const [courses, setCourses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = () => {
    api.get("/general-play/settings").then(res => setSettings(res.data)).catch(() => setToast("Failed to load General Play settings"));
    api.get("/courses").then(res => setCourses(res.data)).catch(() => {});
    setLoadingHistory(true);
    api.get("/general-play/history").then(res => setHistory(res.data)).catch(() => {}).finally(() => setLoadingHistory(false));
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const saveSettings = async (next) => {
    setSaving(true);
    try {
      await api.put("/general-play/settings", next);
      const res = await api.get("/general-play/settings");
      setSettings(res.data);
      showToast("General Play settings saved");
    } catch (e) {
      showToast("Failed: " + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/general-play/history/${id}`);
      setDetail(res.data);
    } catch { showToast("Failed to load round"); }
  };

  if (!settings) return <Layout><div className="p-6 text-gray-500">Loading...</div></Layout>;

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <Layout>
      <div className="p-6 max-w-4xl">
        <h1 className="text-2xl font-bold text-green-700 mb-1">⛳ General Play</h1>
        <p className="text-sm text-gray-500 mb-6">Members register a round on the kiosk, play whenever suits them, then return to enter their score. Recorded for reference only — these rounds do not change anyone's society handicap automatically.</p>

        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Settings</h2>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.enabled} disabled={saving}
                onChange={e => saveSettings({ enabled: e.target.checked, default_course_id: settings.default_course_id })}
                className="w-4 h-4 accent-green-600" />
              <span className="text-sm text-gray-700">Enabled on the kiosk</span>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700 w-40">Default course</label>
            <select value={settings.default_course_id || ""} disabled={saving}
              onChange={e => saveSettings({ enabled: settings.enabled, default_course_id: e.target.value ? parseInt(e.target.value, 10) : null })}
              className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 min-w-[260px]">
              <option value="">— Select a course —</option>
              {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {settings.enabled && !settings.default_course_id && (
            <p className="text-sm mt-3" style={{ color: "#b91c1c" }}>General Play is enabled but has no default course set — the kiosk will refuse registrations until one is picked.</p>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">General Play History</h2>
          {loadingHistory ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-500">No General Play rounds have been registered yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-3">Registered</th>
                  <th className="py-2 pr-3">Member</th>
                  <th className="py-2 pr-3">Course</th>
                  <th className="py-2 pr-3">Handicap</th>
                  <th className="py-2 pr-3">Gross</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map(r => (
                  <tr key={r.id} className="border-b border-gray-100 cursor-pointer hover:bg-gray-50" onClick={() => openDetail(r.id)}>
                    <td className="py-2 pr-3">{fmtDate(r.registered_at)}</td>
                    <td className="py-2 pr-3">{r.player_name}</td>
                    <td className="py-2 pr-3">{r.course_name || "—"}</td>
                    <td className="py-2 pr-3">{r.handicap_snapshot ?? "—"}</td>
                    <td className="py-2 pr-3">{r.gross_total ?? "—"}</td>
                    <td className="py-2 pr-3">
                      {r.status === "completed"
                        ? <span style={{ color: "#166534" }} className="font-semibold">Completed</span>
                        : <span style={{ color: "#92400e" }} className="font-semibold">Awaiting Score</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {toast && <div className="fixed bottom-6 right-6 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm">{toast}</div>}

        {detail && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDetail(null)}>
            <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-lg" onClick={e => e.stopPropagation()}>
              <h2 className="text-xl font-bold text-gray-800 mb-1">{detail.player_name} — General Play</h2>
              <p className="text-sm text-gray-500 mb-4">{detail.course_name} · Registered {fmtDate(detail.registered_at)}{detail.submitted_at ? ` · Submitted ${fmtDate(detail.submitted_at)}` : ""} · Handicap at registration: {detail.handicap_snapshot ?? "—"}</p>
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-1 pr-3">Hole</th>
                    <th className="py-1 pr-3">Par</th>
                    <th className="py-1 pr-3">SI</th>
                    <th className="py-1 pr-3">Gross</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.holes.map(h => (
                    <tr key={h.hole} className="border-b border-gray-100">
                      <td className="py-1 pr-3">{h.hole}</td>
                      <td className="py-1 pr-3">{h.par}</td>
                      <td className="py-1 pr-3">{h.stroke_index}</td>
                      <td className="py-1 pr-3">{h.gross ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-800">Gross total: {detail.gross_total ?? "—"}</span>
                <button onClick={() => setDetail(null)} className="font-semibold py-2 px-5 rounded-lg text-sm bg-gray-200 hover:bg-gray-300">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
